import { z } from 'zod';

const requiredEmailSchema = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() : value),
  z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
);

// Vehicle decoding schemas
export const VinSchema = z.object({
  vin: z.string()
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/i, 'Invalid VIN format')
    .min(17, 'VIN must be 17 characters')
    .max(17, 'VIN must be 17 characters'),
});

export const LicensePlateSchema = z.object({
  licensePlate: z.string()
    .min(2, 'License plate must be at least 2 characters')
    .max(8, 'License plate must be 8 characters or less'),
  state: z.string()
    .min(2, 'State is required')
    .max(2, 'State must be 2 characters'),
});

// Individual step validation schemas
export const TrimConfirmationStepSchema = z.object({
  trim: z.string().min(1, 'Please confirm or enter a trim'),
});

export const MileageStepSchema = z.object({
  mileage: z.number({ coerce: true })
    .min(10, 'Please enter a valid mileage (minimum 10 miles)')
    .max(999999, 'Invalid mileage'),
});

export const ModificationsStepSchema = z.object({
  modifications: z.enum(['yes', 'no'], {
    required_error: 'Please select an option',
  }),
  modificationsDescription: z.string().optional(),
}).refine((data) => {
  if (data.modifications === 'yes' && !data.modificationsDescription) {
    return false;
  }
  return true;
}, {
  message: 'Please describe the modifications',
  path: ['modificationsDescription'],
});

export const OwnershipStepSchema = z.object({
  firstOwner: z.boolean({
    required_error: 'Please select an option',
  }),
});

export const KeysStepSchema = z.object({
  numberOfKeys: z.number({ coerce: true })
    .min(1, 'Number of keys is required')
    .max(10, 'Invalid number of keys'),
});

export const AccidentsStepSchema = z.object({
  accidents: z.enum(['yes', 'no'], {
    required_error: 'Please select an option',
  }),
  accidentDetails: z.string().optional(),
}).refine((data) => {
  if (data.accidents === 'yes' && !data.accidentDetails) {
    return false;
  }
  return true;
}, {
  message: 'Please provide accident details',
  path: ['accidentDetails'],
});

export const ConditionStepSchema = z.object({
  condition: z.enum(['Like New', 'Above Average', 'Average', 'Below Average', 'Rough'], {
    required_error: 'Please select vehicle condition',
  }),
});

export const StartsRunsDrivesStepSchema = z.object({
  startsRunsDrives: z.enum(['yes', 'no'], {
    required_error: 'Please select an option',
  }),
});

export const PreferenceStepSchema = z.object({
  sellOrTrade: z.enum(['Sell it outright', 'Trade it in'], {
    required_error: 'Please select your preference',
  }),
});

export const CommentsStepSchema = z.object({
  comments: z.string()
    .max(1000, 'Comments must be less than 1000 characters')
    .optional(),
});

export const ContactStepSchema = z.object({
  name: z.string()
    .min(2, 'Name is required')
    .max(100, 'Name is too long'),
  email: requiredEmailSchema,
  phone: z.string()
    .regex(/^\d{10}$/, 'Phone number must be 10 digits'),
  postalCode: z.string()
    .regex(/^\d{5}$/, 'Please enter a 5-digit ZIP code')
    .min(5, 'ZIP code must be 5 digits')
    .max(5, 'ZIP code must be 5 digits'),
});

// Complete appraisal submission schema
export const CompleteAppraisalSchema = z.object({
  // Vehicle data
  vin: z.string().length(17),
  year: z.number({ coerce: true }),
  make: z.string().min(1),
  model: z.string().min(1),
  trim: z.string().optional(),
  bodyStyle: z.string().optional(),
  engine: z.string().optional(),
  drivetrain: z.string().optional(),
  transmission: z.string().optional(),
  
  // Step data
  mileage: z.number({ coerce: true }).min(10),
  modifications: z.boolean(),
  modificationsDescription: z.string().optional(),
  accidents: z.boolean(),
  accidentDetails: z.string().optional(),
  condition: z.string().min(1),
  sellOrTrade: z.string().min(1),
  comments: z.string().optional(),
  numberOfKeys: z.number({ coerce: true }).optional(),
  firstOwner: z.boolean().optional(),
  
  // Contact info
  name: z.string().min(2),
  email: requiredEmailSchema,
  phone: z.string().regex(/^\d{10}$/, 'Phone number must be 10 digits'),
  postalCode: z.string().regex(/^\d{5}$/, 'Please enter a 5-digit ZIP code'),
  
  // UTM tracking
  vendor: z.string().optional(),
  utmSource: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmMedium: z.string().optional(),
  utmTerm: z.string().optional(),
  utmContent: z.string().optional(),
  ctaSource: z.string().optional(),
});

// Form step validation mapping
export const STEP_SCHEMAS = {
  trimConfirmation: TrimConfirmationStepSchema,
  mileage: MileageStepSchema,
  modifications: ModificationsStepSchema,
  ownership: OwnershipStepSchema,
  keys: KeysStepSchema,
  accidents: AccidentsStepSchema,
  condition: ConditionStepSchema,
  startsRunsDrives: StartsRunsDrivesStepSchema,
  preference: PreferenceStepSchema,
  comments: CommentsStepSchema,
  contact: ContactStepSchema,
} as const;

export type StepSchemaKeys = keyof typeof STEP_SCHEMAS;
